import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Box, Typography, Grid, Paper, Icon, Tabs, Tab } from "@mui/material";
import { darken } from "@mui/material/styles";
// Updated color variables
const sidebarColor = "#1E3F4F"; // Dark teal for active tiles, matching sidebar
const disabledColor = "#e0e0e0"; // Light gray for disabled tiles
const inProgressColor = "#FFE082"; // Soft pastel yellow for in-progress tiles

// Authorization check
const checkAuthorization = (path) => {
  const user = JSON.parse(localStorage.getItem("user"));
  if (!user || !user.access) return false;

  const accessList = Object.values(user.access).flat();

  const pathMatch = accessList.some((route) => {
    try {
      const routeRegex = new RegExp(`^${route.replace(/:[^\s/]+/g, "([^/]+)")}$`);
      return routeRegex.test(path);
    } catch (err) {
      console.error('Error creating regex:', err.message);
    }
  });

  return pathMatch;
};


// Dashboard component
function Dashboard() {
  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const sections = [
    {
      title: "Operations",
      tiles: [
        { to: "/view-calendar", icon: "calendar_today", label: "Calendar" },
        { to: "/manage-job-orders", icon: "work", label: "Job Orders" },
        { to: "/view-daily-forecast", icon: "wb_twilight", label: "Daily Forecast" },
        { to: "/assign-technicians", icon: "assignment_ind", label: "Technician Assignment" },
        // { to: "/create-job-order", icon: "playlist_add", label: "Job Order" },
        // { to: "/create-endorsement-form", icon: "assignment_turned_in", label: "Create Endorsement Form" },
        { to: "/manage-endorsement-form", icon: "fact_check", label: "Endorsement Forms" },
        { to: "/manage-service-reports", icon: "receipt_long", label: "Service Reports" },
        // { to: "/manage-pending-job-schedule", icon: "schedule", label: "Manage Pending Job Schedule" },

        // { to: "/create-service-report", icon: "post_add", label: "Create Service Report" },
        { to: "/view-productivity-report", icon: "trending_up", label: "Productivity Report" },
        { to: "/view-operation-completion-report", icon: "task_alt", label: "Operation Completion Report" },
        { to: "/view-open-job-orders-report", icon: "pending_actions", label: "Open Job Orders Report" },
        { to: "/create-accomplishment-reports", icon: "note_add", label: "Termite Accomplishment Report" },
        { to: "/manage-technicians", icon: "engineering", label: "Technicians" },
        { to: "/view-competency-skills-matrix", icon: "grid_view", label: "Competency Skills Matrix" },
        { to: "/view-professional-skills-matrix", icon: "school", label: "Professional Skills Matrix" },
        { to: "/view-rest-day", icon: "weekend", label: "Rest Day" },
      ]
    },
    {
      title: "Sales",
      tiles: [
        { to: "/manage-leads", icon: "track_changes", label: "Leads" },
        // { to: "/add-new-lead", icon: "person_add", label: "Add New Lead" },
        { to: "/manage-salesreps", icon: "supervisor_account", label: "Sales Reps" },
        { to: "/view-lead-status-report", icon: "analytics", label: "Lead Status Report" }
      ]
    },

    // {
    //   title: "Finance",
    //   tiles: [
    //     { to: "/manage-billing-records", icon: "request_quote", label: "Manage Billing Records" },
    //     { to: "/create-billing-record", icon: "request_quote", label: "Create Billing Record" },
    //     { to: "/view-aging-invoices", icon: "receipt", label: "View Aging Invoices" }
    //   ]
    // },
    {
      title: "Client Retention",
      tiles: [
        { to: "/manage-clients", icon: "supervised_user_circle", label: "Clients" },
        // { to: "/add-client", icon: "person_add", label: "Add Client" },
        { to: "/view-client-service-reports", icon: "article", label: "Client Service Reports" },
        // { to: "/view-client-billing-records", icon: "request_quote", label: "Client Billing Records" },
        // { to: "/send-job-order-reminder", icon: "alarm", label: "Job Order Reminders" },
        // { to: "/send-billing-reminder", icon: "notifications_active", label: "Billing Reminders" },
        { to: "/send-promotions", icon: "campaign", label: "Promotions" },
        { to: "/list-email-exceptions", icon: "block", label: "Email Exceptions" }
      ]
    },
    {
      title: "Payroll",
      tiles: [
        { to: "/payroll-reports", icon: "payments", label: "Payroll Reports", },
        { to: "/technician-hours", icon: "schedule", label: "Technician Hours", },
        { to: "/technician-compensation", icon: "attach_money", label: "Technician Compensation", }
      ]
    },
    {
      title: "Admin Hub",
      tiles: [
        { to: "/manage-users", icon: "admin_panel_settings", label: "Users" },
        { to: "/manage-access", icon: "vpn_key", label: "Access" },        
        // { to: "/add-user", icon: "person_add_alt_1", label: "Add User" },
        { to: "/delete-technicians", icon: "person_remove", label: "Delete Technicians" },
        { to: "/delete-users", icon: "no_accounts", label: "Delete Users" }
      ]
    }
  ];

  // Filter sections to only show those with authorized tiles
  const authorizedSections = sections.filter(section => {
    const authorizedTiles = section.tiles.filter(tile => tile.alwaysVisible || checkAuthorization(tile.to));
    return authorizedTiles.length > 0;
  });

  // Ensure selectedTab is within bounds of authorized sections
  const validatedSelectedTab = selectedTab >= authorizedSections.length ? 0 : selectedTab;

  // Get the currently selected section and filter its tiles based on authorization
  const currentSection = authorizedSections[validatedSelectedTab];
  const authorizedTiles = currentSection ? 
    currentSection.tiles.filter(tile => tile.alwaysVisible || checkAuthorization(tile.to)) : [];

  // Update selectedTab if it's out of bounds
  React.useEffect(() => {
    if (selectedTab >= authorizedSections.length && authorizedSections.length > 0) {
      setSelectedTab(0);
    }
  }, [authorizedSections.length, selectedTab]);

  return (
    <Box p={3} sx={{ minHeight: "100vh", backgroundColor: "#f4f9f4" }}>
      <Typography
        variant="h5"
        color="#1e5d45"
        gutterBottom
        sx={{ fontFamily: "'Poppins', sans-serif" }}
      >
        Pest Control Management Main Menu
      </Typography>

      <hr style={{ border: "none", height: "2px", backgroundColor: "#1e5d45", marginBottom: "16px" }} />
      
      {/* Tabs for sections */}
      {authorizedSections.length > 0 ? (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs 
            value={validatedSelectedTab} 
            onChange={handleTabChange} 
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                color: '#1e5d45',
                fontWeight: 'bold',
                fontSize: '1rem',
                textTransform: 'none',
                minHeight: 48,
                '&.Mui-selected': {
                  color: '#1E3F4F',
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#1E3F4F',
                height: 3,
              }
            }}
          >
            {authorizedSections.map((section, index) => (
              <Tab key={section.title} label={section.title} />
            ))}
          </Tabs>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No authorized sections available
          </Typography>
        </Box>
      )}

      {/* Display tiles for selected section - only show authorized tiles */}
      {currentSection && authorizedTiles.length > 0 ? (
        <Box mb={4}>
          <Grid container spacing={2}>
            {authorizedTiles.map(tile => (
              <Tile key={tile.to} {...tile} />
            ))}
          </Grid>
        </Box>
      ) : currentSection && authorizedTiles.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No authorized pages available in this section
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

// Section component
function Section({ title, children }) {
  return (
    <Box mb={4}>
      <Typography
        variant="h6"
        color="#1e5d45"
        // sx={{ fontWeight: "bold", mb: 2 }}
        sx={{ fontFamily: "'Poppins', sans-serif", mb: 1 }}
      >
        {title}
      </Typography>
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Box>
  );
}

// Tile component
function Tile({ to, icon, label, disabled }) {
  const tileColor = disabled ? disabledColor : sidebarColor;
  const textColor = disabled ? "gray" : "white";

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Paper
        component={disabled ? "div" : Link}
        to={disabled ? undefined : to}
        elevation={3}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          height: 140,
          backgroundColor: tileColor,
          color: textColor,
          textDecoration: "none",
          borderRadius: 2,
          cursor: disabled ? "not-allowed" : "pointer",
          "&:hover": {
            transform: disabled ? "none" : "translateY(-5px)",
            boxShadow: disabled ? "none" : 8,
            backgroundColor: disabled ? disabledColor : darken(tileColor, 0.1)
          }
        }}
      >
        <Icon sx={{ fontSize: 80, mb: 1 }}>{icon}</Icon>
        <Typography 
          variant="body1" 
          fontWeight="500"
          sx={{
            textAlign: "center",
            wordWrap: "break-word",
            hyphens: "auto",
            lineHeight: 1.2
          }}
        >
          {label}
        </Typography>
      </Paper>
    </Grid>
  );
}

export default Dashboard;
